import { Zap } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { formatShortDate, usePlan } from "@/lib/plan";

// The persistent "what's left this week" counter in the sidebar. Reads the
// plan stub in lib/plan.ts — see there before trusting the numbers.
export function QuotaMeter({ onUpgrade, className }: { onUpgrade: () => void; className?: string }) {
  const { isUnlimited, used, limit, remaining, resetsAt } = usePlan();

  if (isUnlimited) {
    return (
      <div className={cn("flex items-center gap-2.5 rounded-xl bg-accent p-3", className)}>
        <Zap aria-hidden="true" className="h-4 w-4 shrink-0 text-primary" />
        <div>
          <p className="text-sm font-semibold text-accent-foreground">Waggle Pro</p>
          <p className="text-xs text-muted-foreground">Unlimited AI actions</p>
        </div>
      </div>
    );
  }

  const isOut = remaining === 0;
  const isLow = remaining <= 3;

  return (
    <div className={cn("rounded-xl border p-3", isOut ? "bg-red-50" : "bg-muted/60", className)}>
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-sm font-semibold">AI actions</p>
        <p className="text-sm tabular-nums text-muted-foreground">
          <span className={cn("font-semibold", isOut ? "text-red-700" : "text-foreground")}>{remaining}</span> of {limit} left
        </p>
      </div>
      <Progress
        value={Math.min(100, (used / limit) * 100)}
        aria-label="AI actions used this week"
        aria-valuetext={`${remaining} of ${limit} left this week`}
        className={cn(
          "mt-2 h-2 bg-border",
          isOut ? "[&>div]:bg-red-600" : isLow ? "[&>div]:bg-amber-500" : undefined,
        )}
      />
      <p className="mt-2 text-xs text-muted-foreground">Resets {formatShortDate(resetsAt)}</p>
      <button
        type="button"
        onClick={onUpgrade}
        className="mt-1 inline-flex items-center gap-1.5 rounded-xs text-sm font-semibold text-primary hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring/50"
      >
        {isOut ? "Upgrade to keep going" : "Upgrade for unlimited"} <Zap aria-hidden="true" className="h-4 w-4" />
      </button>
    </div>
  );
}
